/**
 * CookieConsentBanner — bottom-sticky GDPR cookie banner shown until the
 * visitor accepts or rejects optional cookies. Essential cookies are always
 * on and don't require consent.
 *
 * Renders nothing once a decision is recorded. Fine-grained per-category
 * preferences live in Settings → Privacy & Cookies (no separate modal).
 */
import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';

import {
  getConsent, getConsentRecord, setConsent, CONSENT_CHANGED_EVENT,
} from '../services/cookieConsent';

export default function CookieConsentBanner() {
  const [decision, setDecision] = useState(() => getConsent());
  const [isStale, setIsStale]   = useState(() => !!getConsentRecord()?.is_stale);

  useEffect(() => {
    const onChange = (e) => {
      setDecision(e.detail?.decision ?? getConsent());
      setIsStale(!!getConsentRecord()?.is_stale);
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
  }, []);

  if (decision === 'accepted' || decision === 'declined' || decision === 'customized') return null;

  const heading = isStale ? 'We updated our cookie policy' : 'We use cookies';
  const body = isStale
    ? 'Our privacy policy has changed since you last visited. Please confirm your cookie preferences to continue using analytics features.'
    : 'Essential cookies keep you signed in and are always on. With your permission, analytics cookies help us understand how the platform is used. You can adjust individual categories any time in Account Settings.';

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 inset-x-0 z-[70] px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-4xl bg-white/95 backdrop-blur-md border border-stone-200 shadow-2xl rounded-2xl px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 bg-orange-50 rounded-full flex items-center justify-center text-orange-500">
              <Cookie size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-poppins font-semibold text-gray-900 mb-1">
                {heading}
              </h2>
              <p className="text-xs sm:text-[13px] text-gray-600 font-nunito leading-relaxed">
                {body}{' '}
                <a
                  href="/privacy"
                  className="underline text-[#004B00] hover:text-[#003800] font-semibold"
                >
                  Learn more
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 sm:ml-4">
            <button
              type="button"
              onClick={() => setConsent('declined')}
              data-testid="cookie-consent-decline-btn"
              className="text-xs sm:text-sm px-4 py-2 rounded-full border border-stone-300 text-gray-700 hover:bg-stone-50 font-nunito font-semibold transition-all whitespace-nowrap"
            >
              Reject optional
            </button>
            <button
              type="button"
              onClick={() => setConsent('accepted')}
              data-testid="cookie-consent-accept-btn"
              className="text-xs sm:text-sm px-4 py-2 rounded-full bg-[#004B00] hover:bg-[#003800] text-white font-nunito font-semibold shadow-sm transition-all whitespace-nowrap"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={() => setConsent('declined')}
              aria-label="Dismiss cookie banner"
              data-testid="cookie-consent-close-btn"
              className="ml-1 w-8 h-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-stone-100 flex items-center justify-center transition-colors sm:hidden"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
